// LayerVisibilityPreference — レイヤーの表示/非表示 (👁) を **room 単位** で
// localStorage に永続化する infrastructure 層ユーティリティ.
//
// ## 背景 (feedback-triage-2026-08-12.md §1 B-2)
//
// 2026-08-07 のユーザー報告「消したはずのレイヤー3の線が入り直したら半端に復活して
// 今まで描いてた線と重なってしまいました」の調査結果:
//
//   - サーバの snapshot PNG も welcome delta もクライアントのライブ再生も **すべて正しい**
//     ことを実データで確認した (mask 限定画素差分 / クライアント再生比較)。データ破損は無い。
//   - 絵ログに `whiteboard.clear` は 1 件も無く、Undo (103 件) は正しく処理されていた。
//   - 一方 **レイヤー可視は `EngineCore.visibility` のインメモリ状態のみ** で、
//     `new Array(layerCount).fill(true)` で初期化される (engineCore.ts:143)。永続化が無い。
//     → **👁 で非表示にしたレイヤーは、入り直すと必ず表示に戻る。**
//
// 日本語の「消した」は「画面から消した = 非表示にした」を普通に含む。つまり報告は
// **自分で非表示にした → 非表示にしたことを忘れた → 再入室で表示に戻っていた** と読むのが
// 素直で、観測されたデータのすべてと整合する (作者判断 2026-08-12)。
//
// ## なぜ「黙って復元」しないのか (作者判断 2026-08-12)
//
// 単に復元すると「なぜレイヤー3 が出ないんだ」という **同じ混乱が別の形で起きる**
// (根本原因は「隠したことを忘れる」ことなので、黙って復元しても忘れたままになる)。
//
// さらに決定的な理由: **共有キャンバスなので、留守中に他の人がそのレイヤーへ描いている
// 可能性がある**。前回の可視状態が今のキャンバスにふさわしいとは限らず、黙って復元すると
// **他人の作業を本人に無断で隠す**ことになる。
//
// → 保存はするが、**復元は必ず本人の選択を経る**。入室時に非表示レイヤーがあるときだけ
//    確認ダイアログを出し、「非表示のままにする」/「すべて表示する」を選ばせる。
//    無視・閉じられた場合は **表示側に倒す** (黙って隠すより黙って見せるほうが安全)。
//
// ## 設計
//
// - `previewSizePreference.ts` / `chatFontScalePreference.ts` と **同流儀** の
//   StorageLike 抽象 + probe + storage-injection test。
// - 保存単位は **room**。部屋は 7〜30 日で失効する (`retention-communication-2026-07-14.md`)
//   ので、放置するとキーが単調増加する。**直近 `MAX_ROOMS` 部屋だけ LRU で保持**する。
// - **broadcast しない** (`ModernLayerPanel.tsx:15` の「ローカル、broadcast しない」方針を
//   維持。他人の画面を勝手に変えない)。
// - SSR / Safari private browsing 厳格モードでも getter は空を返すだけ、setter は no-op。

/** localStorage キー (= 外から観察可能な "user data" なので値を固定). */
export const LAYER_VISIBILITY_KEY = 'oekaki:layer-visible';

/**
 * 保持する room 数の上限。超えたら **最後に触った順** で古いものから捨てる。
 * 部屋は 7〜30 日で失効するので、実用上これだけあれば「行きつけの部屋」は覆える。
 */
export const MAX_ROOMS = 20;

/** `layerId` → 表示中か。**`true` のエントリは保存しない** (既定が表示なので冗長)。 */
export type LayerVisibilityMap = Readonly<Record<number, boolean>>;

/** 永続化される 1 room 分のレコード。`at` は LRU 用の最終更新時刻 (epoch ms)。 */
interface RoomEntry {
  readonly hidden: readonly number[];
  readonly at: number;
}

/** Minimal Storage shape (`uiScalePreference.ts` 等と同流儀)。 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * 可視 map から **非表示の layerId だけ** を昇順で取り出す純粋関数。
 * 保存形式を「非表示 id の配列」にしているのは、既定 (= 全表示) との差分だけを
 * 持てば十分で、レイヤー枚数が将来増えても記録が膨らまないため。
 */
export const hiddenLayerIdsOf = (map: LayerVisibilityMap): number[] =>
  Object.entries(map)
    .filter(([, visible]) => visible === false)
    .map(([id]) => Number(id))
    .filter((id) => Number.isInteger(id) && id >= 0)
    .sort((a, b) => a - b);

/** 非表示 id 配列 → 可視 map。存在しない id は「表示」扱い (= 既定)。 */
export const visibilityMapOf = (hidden: readonly number[]): Record<number, boolean> => {
  const out: Record<number, boolean> = {};
  for (const id of hidden) out[id] = false;
  return out;
};

/**
 * UI 表示名。**engine の layerId は 0 始まり、UI は「レイヤー1」始まり**
 * (`w3-classic-layers.md §2.1`「上から レイヤー3(前) / レイヤー2 / レイヤー1(背)」)。
 * 確認ダイアログで具体名を出すために使う (作者判断 2026-08-12: 総称ではなく具体名。
 * 「どのレイヤーを隠したか思い出せない」ことが今回の問題の核なので名前を出す価値が高い)。
 */
export const layerDisplayName = (layerId: number): string => `レイヤー${layerId + 1}`;

/** 非表示レイヤー群を「レイヤー2、レイヤー3」の形に整形する。 */
export const formatHiddenLayerNames = (hidden: readonly number[]): string =>
  hidden.map(layerDisplayName).join('、');

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:layer-visible__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

/** 壊れ値に強い parse。失敗時は空 (= 全部屋とも既定)。例外は投げない。 */
const readAll = (storage: StorageLike): Record<string, RoomEntry> => {
  try {
    const raw = storage.getItem(LAYER_VISIBILITY_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: Record<string, RoomEntry> = {};
    for (const [roomId, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v !== 'object' || v === null) continue;
      const { hidden, at } = v as { hidden?: unknown; at?: unknown };
      if (!Array.isArray(hidden)) continue;
      const ids = hidden.filter((n): n is number => Number.isInteger(n) && (n as number) >= 0);
      out[roomId] = { hidden: ids, at: typeof at === 'number' ? at : 0 };
    }
    return out;
  } catch {
    return {};
  }
};

/**
 * LRU で `MAX_ROOMS` に収める純粋関数。`at` 降順で残す (同値は roomId 昇順で安定化)。
 * export しているのは境界 (ちょうど上限 / 超過 1) をテストで直接固定するため。
 */
export const pruneToLimit = (
  all: Record<string, RoomEntry>,
  limit = MAX_ROOMS,
): Record<string, RoomEntry> => {
  const entries = Object.entries(all);
  if (entries.length <= limit) return all;
  entries.sort((a, b) => b[1].at - a[1].at || a[0].localeCompare(b[0]));
  return Object.fromEntries(entries.slice(0, limit));
};

export interface LayerVisibilityStore {
  /** その room の **非表示 layerId** (昇順)。不在 / storage 不可なら空配列。 */
  getHidden(roomId: string): number[];
  /** その room の可視状態を保存。全表示なら行ごと削除する (ゴミを残さない)。 */
  set(roomId: string, map: LayerVisibilityMap): void;
  /** その room の記録を消す (=「すべて表示する」を選んだとき)。 */
  clear(roomId: string): void;
}

export interface CreateLayerVisibilityStoreOptions {
  /** 省略時は `globalThis.localStorage` を probe。`null` で強制 no-op (テスト用)。 */
  storage?: StorageLike | null;
  /** LRU 用の現在時刻。テストで固定するために注入可能。 */
  now?: () => number;
}

export const createLayerVisibilityStore = (
  opts: CreateLayerVisibilityStoreOptions = {},
): LayerVisibilityStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());
  const now = opts.now ?? Date.now;

  const write = (all: Record<string, RoomEntry>): void => {
    if (storage === null) return;
    try {
      storage.setItem(LAYER_VISIBILITY_KEY, JSON.stringify(pruneToLimit(all)));
    } catch {
      // QuotaExceeded 等 — 永続化できないだけで動作は継続させる。
    }
  };

  return {
    getHidden(roomId) {
      if (storage === null) return [];
      return [...(readAll(storage)[roomId]?.hidden ?? [])].sort((a, b) => a - b);
    },
    set(roomId, map) {
      if (storage === null) return;
      const hidden = hiddenLayerIdsOf(map);
      const all = readAll(storage);
      if (hidden.length === 0) {
        if (all[roomId] === undefined) return;
        delete all[roomId];
      } else {
        all[roomId] = { hidden, at: now() };
      }
      write(all);
    },
    clear(roomId) {
      if (storage === null) return;
      const all = readAll(storage);
      if (all[roomId] === undefined) return;
      delete all[roomId];
      write(all);
    },
  };
};
